import { HULL_MODEL_VERSION } from "@frontier/content/hull-models";
import { shipFittingInstallSchema, type ShipFitting, type FittingUpgradeTarget } from "@frontier/content/ship-fitting";
import { shopOrderInputSchema, shopOrderViewSchema, shopViewSchema } from "@frontier/content/shop";
import { seasonViewSchema, seasonClaimInputSchema, seasonUnlockInputSchema, type PaintId } from "@frontier/content/season";
import { supplyStationViewSchema, supplyStationResultSchema } from "@frontier/protocol/supplies";
import { supplyPurchaseInputSchema, supplyTransferInputSchema, type SupplyPurchaseInput, type SupplyTransferInput } from "@frontier/content/supplies";
import { z } from "zod";
import { CLIENT_PROTOCOL_HEADERS } from "@frontier/protocol/client-compatibility";
import { upgradePricesSchema } from "@frontier/protocol/upgrade-prices";
import {
  registerRequestSchema,
  loginRequestSchema,
  logoutResponseSchema,
  vkPlayLaunchRequestSchema,
  vkPlayConfigSchema,
  vkGamesLaunchRequestSchema,
  vkGamesConfigSchema,
  factionRequestSchema,
  profileNameRequestSchema,
} from "@frontier/protocol/auth-contracts";
import type {
  RegisterRequest,
  LoginRequest,
  FactionRequest,
} from "@frontier/protocol/auth-contracts";
import {
  profileSchema,
  sessionResponseSchema,
} from "@frontier/protocol/contracts";
import type {
  EquipmentKey,
  HullId,
  LoadoutId,
  ModuleId,
  PassiveId,
  WeaponId,
} from "@frontier/protocol/contracts";
import type { ContractSelection } from "@frontier/content/contracts";
import type {
  EquipmentTier,
  EquipmentTiers,
} from "@frontier/content/equipment-tiers";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
  }
}

export function requestId(): string {
  const values = new Uint32Array(4);
  crypto.getRandomValues(values);
  return Array.from(values, (value) =>
    value.toString(16).padStart(8, "0"),
  ).join("-");
}

async function request<T>(
  path: string,
  token: string,
  schema: z.ZodType<T>,
  body?: string,
  signal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 15000);
  try {
    const response = await fetch(path, {
      method: body === undefined ? "GET" : "POST",
      headers: {
        ...CLIENT_PROTOCOL_HEADERS,
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const error = z
        .object({ message: z.string(), code: z.string().optional() })
        .safeParse(await response.json());
      throw new ApiError(
        error.success
          ? error.data.message
          : "Игра временно недоступна.",
        response.status,
        error.success ? error.data.code : undefined,
      );
    }
    const parsed = schema.safeParse(await response.json());
    if (!parsed.success) {
      console.warn("api-response-invalid", { path, issues: parsed.error.issues.map(issue => ({ code: issue.code, path: issue.path.join(".") })) });
      throw new Error("Не удалось прочитать ответ игры. Обнови страницу и повтори попытку.");
    }
    return parsed.data;
  } catch (error) {
    if (controller.signal.aborted && !signal?.aborted)
      throw new Error("Нет ответа от игры. Повтори подключение.");
    if (error instanceof SyntaxError)
      throw new Error("Не удалось прочитать ответ игры. Обнови страницу и повтори попытку.");
    if (error instanceof TypeError)
      throw new Error("Нет связи с игрой. Проверь подключение и повтори попытку.");
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

export const api = {
  installFitting: (token: string, fitting: ShipFitting, operationId: string) =>
    request("/api/fitting", token, profileSchema, JSON.stringify(shipFittingInstallSchema.parse({ fitting, requestId: operationId }))),
  shop: (token: string) => request("/api/shop", token, shopViewSchema),
  createShopOrder: (token: string, input: z.infer<typeof shopOrderInputSchema>) => request("/api/shop/orders", token, shopOrderViewSchema, JSON.stringify(shopOrderInputSchema.parse(input))),
  startShopCheckout: (token: string, id: string, attemptId: string) => request(`/api/shop/orders/${encodeURIComponent(id)}/start`, token, shopOrderViewSchema, JSON.stringify({ attemptId })),
  closeShopOrder: (token: string, id: string) => request(`/api/shop/orders/${encodeURIComponent(id)}/close`, token, shopOrderViewSchema, "{}"),
  shopOrder: (token: string, id: string) => request(`/api/shop/orders/${encodeURIComponent(id)}`, token, shopOrderViewSchema),
  season: (token: string) => request("/api/season", token, seasonViewSchema),
  claimSeason: (token: string, input: z.infer<typeof seasonClaimInputSchema>) => request("/api/season/claim", token, profileSchema, JSON.stringify(seasonClaimInputSchema.parse(input))),
  unlockSeason: (token: string, input: z.infer<typeof seasonUnlockInputSchema>) => request("/api/season/unlock", token, profileSchema, JSON.stringify(seasonUnlockInputSchema.parse(input))),
  selectPaint: (token: string, paintId: PaintId) => request("/api/paint", token, profileSchema, JSON.stringify({ paintId })),
  supplies: (token: string) => request("/api/supplies", token, supplyStationViewSchema),
  purchaseSupply: (token: string, input: SupplyPurchaseInput) => request("/api/supplies/purchase", token, supplyStationResultSchema, JSON.stringify(supplyPurchaseInputSchema.parse(input))),
  transferSupply: (token: string, input: SupplyTransferInput) => request("/api/supplies/transfer", token, supplyStationResultSchema, JSON.stringify(supplyTransferInputSchema.parse(input))),
  upgradePrices: (signal?: AbortSignal) =>
    request("/api/upgrade-prices", "", upgradePricesSchema, undefined, signal),
  vkGamesConfig: () =>
    request("/api/platform/vkgames", "", vkGamesConfigSchema),
  vkGamesSession: (
    launchParams: string,
    guestToken = "",
    displayName?: string,
  ) =>
    request(
      "/api/auth/vkgames",
      guestToken,
      sessionResponseSchema,
      JSON.stringify(
        vkGamesLaunchRequestSchema.parse({ launchParams, displayName }),
      ),
    ),
  vkPlayConfig: () => request("/api/platform/vkplay", "", vkPlayConfigSchema),
  vkPlaySession: (launchParams: string, guestToken = "") =>
    request(
      "/api/auth/vkplay",
      guestToken,
      sessionResponseSchema,
      JSON.stringify(vkPlayLaunchRequestSchema.parse({ launchParams })),
    ),
  chooseFaction: (token: string, input: FactionRequest) =>
    request(
      "/api/auth/faction",
      token,
      sessionResponseSchema,
      JSON.stringify(factionRequestSchema.parse(input)),
    ),
  register: (token: string, input: RegisterRequest) =>
    request(
      "/api/auth/register",
      token,
      sessionResponseSchema,
      JSON.stringify(registerRequestSchema.parse(input)),
    ),
  login: (input: LoginRequest) =>
    request(
      "/api/auth/login",
      "",
      sessionResponseSchema,
      JSON.stringify(loginRequestSchema.parse(input)),
    ),
  logout: (token: string) =>
    request("/api/auth/logout", token, logoutResponseSchema, "{}"),
  session: (token: string) =>
    request(
      "/api/session",
      "",
      sessionResponseSchema,
      JSON.stringify(token ? { token } : {}),
    ),
  profile: (token: string, signal?: AbortSignal) =>
    request("/api/profile", token, profileSchema, undefined, signal),
  renameProfile: (token: string, name: string) =>
    request(
      "/api/profile/name",
      token,
      profileSchema,
      JSON.stringify(profileNameRequestSchema.parse({ name })),
    ),
  loadout: (
    token: string,
    loadoutId: LoadoutId,
    weaponId?: WeaponId,
    moduleId?: ModuleId,
    passiveId?: PassiveId,
    secondaryModuleId?: ModuleId,
    hullId?: HullId,
    equipmentTiers?: Partial<EquipmentTiers>,
  ) =>
    request(
      "/api/loadout",
      token,
      profileSchema,
      JSON.stringify({
        loadoutId,
        weaponId,
        moduleId,
        passiveId,
        secondaryModuleId,
        hullId,
        equipmentTiers,
      }),
    ),
  selectHull: (token: string, hullId: HullId) => request("/api/hulls/select", token, profileSchema, JSON.stringify({ hullId })),
  purchaseHull: (token: string, hullId: HullId, operationId: string) =>
    request(
      "/api/hulls/purchase",
      token,
      profileSchema,
      JSON.stringify({ hullId, requestId: operationId, priceVersion: HULL_MODEL_VERSION }),
    ),
  craftEquipment: (token: string, key: EquipmentKey, operationId: string) =>
    request(
      "/api/arsenal/craft",
      token,
      profileSchema,
      JSON.stringify({ key, requestId: operationId }),
    ),
  dismantleEquipment: (
    token: string,
    key: EquipmentKey,
    tier: EquipmentTier,
    operationId: string,
  ) =>
    request(
      "/api/arsenal/dismantle",
      token,
      profileSchema,
      JSON.stringify({ key, tier, requestId: operationId }),
    ),
  trainingContract: (token: string) => request("/api/tutorial/contract", token, profileSchema, JSON.stringify({})),
  contracts: (token: string, contracts: ContractSelection[]) =>
    request(
      "/api/contracts",
      token,
      profileSchema,
      JSON.stringify({ contracts }),
    ),
  upgradeEquipment: (
    token: string,
    key: EquipmentKey,
    fromTier: EquipmentTier,
    operationId: string,
    priceVersion?: string,
    mount?: FittingUpgradeTarget,
  ) =>
    request(
      "/api/upgrade",
      token,
      profileSchema,
      JSON.stringify({
        key,
        fromTier,
        requestId: operationId,
        ...(priceVersion === undefined ? {} : { priceVersion }),
        ...(mount ? { mount } : {}),
      }),
    ),
  feedback: (token: string, text: string, rating: number) =>
    request(
      "/api/feedback",
      token,
      z.object({ ok: z.boolean() }),
      JSON.stringify({ text, rating }),
    ),
};
